import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FieldBaseClasses, FieldOpenClasses } from '@/components/ui/fieldStyles';
import { getFieldLabelId } from '@/lib/formErrors';
import { cn } from '@/lib/utils';

const TypeaheadResetMs = 750;
const OpenKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' '];

function isPrintableKey(key) {
  return key.length === 1 && key.trim().length > 0;
}

export function Select({ id, options, value, onChange, onBlur, placeholder = 'Select an option', className, ...triggerProps }) {
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const typeaheadRef = useRef({ query: '', timeoutId: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listboxId = `${id}-listbox`;
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;
  const lastIndex = options.length - 1;

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    listRef.current?.children[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, highlightedIndex]);

  useEffect(() => {
    const typeahead = typeaheadRef.current;
    return () => window.clearTimeout(typeahead.timeoutId);
  }, []);

  function getOptionId(optionIndex) {
    return `${id}-option-${optionIndex}`;
  }

  function openList(preferredIndex) {
    setHighlightedIndex(preferredIndex ?? Math.max(selectedIndex, 0));
    setIsOpen(true);
  }

  function selectOption(optionIndex) {
    onChange(options[optionIndex].value);
    setIsOpen(false);
  }

  function findTypeaheadMatch(character) {
    const typeahead = typeaheadRef.current;
    window.clearTimeout(typeahead.timeoutId);
    typeahead.query = `${typeahead.query}${character.toLowerCase()}`;
    typeahead.timeoutId = window.setTimeout(() => {
      typeahead.query = '';
    }, TypeaheadResetMs);
    return options.findIndex((option) => option.label.toLowerCase().startsWith(typeahead.query));
  }

  function handleKeyDown(event) {
    if (!isOpen) {
      if (OpenKeys.includes(event.key)) {
        event.preventDefault();
        openList();
      } else if (isPrintableKey(event.key)) {
        const matchIndex = findTypeaheadMatch(event.key);
        if (matchIndex >= 0) openList(matchIndex);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((currentIndex) => Math.min(currentIndex + 1, lastIndex));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((currentIndex) => Math.max(currentIndex - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setHighlightedIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setHighlightedIndex(lastIndex);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (highlightedIndex >= 0) selectOption(highlightedIndex);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    } else if (event.key === 'Tab') {
      setIsOpen(false);
    } else if (isPrintableKey(event.key)) {
      const matchIndex = findTypeaheadMatch(event.key);
      if (matchIndex >= 0) setHighlightedIndex(matchIndex);
    }
  }

  function handleTriggerClick() {
    if (isOpen) {
      setIsOpen(false);
    } else {
      openList();
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={isOpen && highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        className={cn(FieldBaseClasses, 'flex h-12 items-center justify-between gap-3 px-4 text-left', isOpen && FieldOpenClasses, className)}
        {...triggerProps}
      >
        <span className={cn('truncate', !selectedOption && 'text-steel/80')}>{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown aria-hidden="true" className={cn('size-5 shrink-0 text-steel transition-transform duration-200', isOpen && 'rotate-180')} />
      </button>
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-labelledby={getFieldLabelId(id)}
        hidden={!isOpen}
        className="absolute inset-x-0 top-full z-40 mt-2 max-h-72 overflow-y-auto rounded-lg border border-grid bg-paper p-1 shadow-[0_24px_48px_-24px_rgb(43_48_44/0.45)]"
      >
        {options.map((option, optionIndex) => {
          const isSelected = option.value === value;
          return (
            <li
              key={option.value}
              id={getOptionId(optionIndex)}
              role="option"
              aria-selected={isSelected}
              onPointerDown={(event) => event.preventDefault()}
              onPointerMove={() => setHighlightedIndex(optionIndex)}
              onClick={() => selectOption(optionIndex)}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2.5 text-[0.9375rem] text-graphite',
                optionIndex === highlightedIndex && 'bg-pad-light',
                isSelected && 'font-semibold'
              )}
            >
              <span>{option.label}</span>
              {isSelected ? <Check aria-hidden="true" strokeWidth={3} className="size-4 shrink-0 text-redline" /> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}